import 'react-app-polyfill/ie9'
import 'react-app-polyfill/ie11'
import 'react-app-polyfill/stable'

import React from 'react'
import ReactDOM from 'react-dom'
import './index.scss'
import App from './App'
import * as serviceWorker from './serviceWorker'

import { BrowserRouter, MemoryRouter } from 'react-router-dom'
import { IntlReducer as Intl, IntlProvider, IntlActions } from 'react-redux-multilingual'
import { SnackbarProvider } from 'notistack'
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import MomentUtils from '@date-io/moment'
import { Provider } from 'react-redux'
import { createStore, applyMiddleware, combineReducers, compose } from 'redux'
import thunk from 'redux-thunk'

import translations from './lang'

import authReducer from './store/reducers/auth'
import companyReducer from './store/reducers/company'
import perspectiveReducer from './store/reducers/perspective'
import roleReducer from './store/reducers/role'
import formReducer from './store/reducers/form'
import levelReducer from './store/reducers/level'
import structureReducer from './store/reducers/structure'
import passwordPolicyReducer from './store/reducers/passwordPolicy'
import emailServerReducer from './store/reducers/emailServer'
import positionReducer from './store/reducers/position'
import positionGroupReducer from './store/reducers/positionGroup'
import kpiTypeReducer from './store/reducers/kpiType'
import uomReducer from './store/reducers/uom'
import appraisalItemReducer from './store/reducers/appraisalItem'
import organizationReducer from './store/reducers/organization'
import homeReducer from './store/reducers/home'
import criteriaReducer from './store/reducers/criteria'
import userReducer from './store/reducers/user'
import employeeReducer from './store/reducers/employee'
import individualReducer from './store/reducers/individualAssignment'
import corporateReducer from './store/reducers/corporateAssignment'
import assignmentReducer from './store/reducers/assignmentFlow'
import alertReducer from './store/reducers/alert'
import profileReducer from './store/reducers/profile'
import appraisalReducer from './store/reducers/appraisal'
import forceRankReducer from './store/reducers/forceRank'
import levelGroupReducer from './store/reducers/levelGroup'
import thresholdGroupReducer from './store/reducers/thresholdGroup'
import competencyScoreReducer from './store/reducers/competencyScore'
import usageLog from './store/reducers/usageLog'
import horizontalForceRankReducer from './store/reducers/horizontalForceRank'
import verticalForceRankReducer from './store/reducers/verticalForceRank'
import feedbackReducer from './store/reducers/feedback'

import reportDataEntry from './store/reducers/report/dataEntry'
import reportKpi from './store/reducers/report/kpi'
import reportAppraisal from './store/reducers/report/appraisal'
import reportPerformanceform from './store/reducers/report/performanceform'
import dashboardOverallPerformance from './store/reducers/dashboardOverallPerformance'
import dashboardPerformanceComparison from './store/reducers/dashboardPerformanceComparison'
import dashboardPerformanceTrend from './store/reducers/dashboardPerformanceTrend'
import dashboardBranchPerformance from './store/reducers/dashboardBranchPerformance'
import forceRankGroup from './store/reducers/forceRankGroup'
import reportAppraisalByOrg from './store/reducers/report/appraisalByOrg'
import reportAppraisalByLevel from './store/reducers/report/appraisalByLevel'
import reportAppraisalByJobKpi from'./store/reducers/report/appraisalByJobKpi'
import reportBonusSalary from './store/reducers/report/bonusSalary'
import { feedbackReportReducer, gradeCriteriaReducer } from './store/reducers'
import { importSalaryReducer } from './store/reducers'
import { library } from '@fortawesome/fontawesome-svg-core'
import icons from './icons'
import { autoSignIn } from './store/actions'

library.add(icons)

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose

const rootReducers = combineReducers({
  auth: authReducer,
  companies: companyReducer,
  competencyScore: competencyScoreReducer,
  perspective: perspectiveReducer,
  roles: roleReducer,
  form: formReducer,
  level: levelReducer,
  structure: structureReducer,
  passwordPolicy: passwordPolicyReducer,
  position: positionReducer,
  positionGroup: positionGroupReducer,
  kpiType: kpiTypeReducer,
  uom: uomReducer,
  organization: organizationReducer,
  criteria: criteriaReducer,
  user: userReducer,
  emailServer: emailServerReducer,
  appraisalItems: appraisalItemReducer,
  employees: employeeReducer,
  home: homeReducer,
  individualAssignment: individualReducer,
  corporateAssignment: corporateReducer,
  assignmentFlow: assignmentReducer,
  alert: alertReducer,
  profile: profileReducer,
  appraisal: appraisalReducer,
  forceRank: forceRankReducer,
  levelGroup: levelGroupReducer,
  thresholdGroup: thresholdGroupReducer,
  usageLog: usageLog,
  reportDataEntry: reportDataEntry,
  reportKpi: reportKpi,
  reportAppraisal: reportAppraisal,
  dashboardOverallPerformance: dashboardOverallPerformance,
  reportPerformanceform: reportPerformanceform,
  dashboardPerformanceComparison: dashboardPerformanceComparison,
  dashboardPerformanceTrend: dashboardPerformanceTrend,
  dashboardBranchPerformance: dashboardBranchPerformance,
  gradeCriteria: gradeCriteriaReducer,
  importSalary: importSalaryReducer,
  horizontalForceRank: horizontalForceRankReducer,
  verticalForceRank: verticalForceRankReducer,
  forceRankGroup: forceRankGroup,
  reportAppraisalByOrg: reportAppraisalByOrg,
  reportAppraisalByLevel: reportAppraisalByLevel,
  reportAppraisalByJobKpi: reportAppraisalByJobKpi,
  reportBonusSalary: reportBonusSalary,
  feedback: feedbackReducer,
  feedbackReport: feedbackReportReducer,
  Intl
})

export const store = createStore(rootReducers, composeEnhancers(applyMiddleware(thunk)))

// Set Auth
store.dispatch(autoSignIn())

// Set Language
const initialLanguage = localStorage.getItem('language') || 'en'
store.dispatch(IntlActions.setLocale(initialLanguage))

const routerMode = process.env.REACT_APP_ROUTER_MODE || 'default'
const appUrlPrefix = process.env.REACT_APP_APP_URL_PREFIX || 'app'

let appWithRouter = (
  <BrowserRouter basename={`/${appUrlPrefix}`}>
    <App />
  </BrowserRouter>
)

switch (routerMode) {
  case 'memory':
    appWithRouter = (
      <MemoryRouter>
        <App />
      </MemoryRouter>
    )
    break
  default:
    break
}

const ConfiguredApp = (
  <Provider store={store}>
    <IntlProvider translations={translations} locale='en'>
      <SnackbarProvider>
        <MuiPickersUtilsProvider utils={MomentUtils}>{appWithRouter}</MuiPickersUtilsProvider>
      </SnackbarProvider>
    </IntlProvider>
  </Provider>
)

ReactDOM.render(ConfiguredApp, document.getElementById('root'))

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister()
